/**
 * Интерактивный блок стратегии лендинга.
 * Расчёт метрик зеркалит логику server/raceEngine.js (computeTeamModifiers) на сухой трассе.
 */
let THREE;
let OrbitControls;
let GLTFLoader;
let RoomEnvironment;
let MeshoptDecoder;
let threeDependenciesPromise;

export async function loadThreeDependencies() {
  if (!threeDependenciesPromise) {
    threeDependenciesPromise = Promise.all([
      import("three"),
      import("three/addons/controls/OrbitControls.js"),
      import("three/addons/loaders/GLTFLoader.js"),
      import("three/addons/environments/RoomEnvironment.js"),
      // Meshopt-сжатый ferrari_sf25.min.glb (EXT_meshopt_compression)
      import("three/addons/libs/meshopt_decoder.module.js")
    ]).then(([threeModule, controlsModule, loaderModule, environmentModule, meshoptModule]) => {
      THREE = threeModule;
      OrbitControls = controlsModule.OrbitControls;
      GLTFLoader = loaderModule.GLTFLoader;
      RoomEnvironment = environmentModule.RoomEnvironment;
      MeshoptDecoder = meshoptModule.MeshoptDecoder;
      // WASM decoder must be ready before GLTFLoader touches meshopt buffers
      if (MeshoptDecoder?.ready) return MeshoptDecoder.ready;
    });
  }
  return threeDependenciesPromise;
}

const SPEED_POINTS_FACTOR = 0.0006;
const BASE_BUDGET = 1_000_000;
// Meshopt + WebP (free license). Fallback на полный GLB при ошибке загрузки.
const MODEL_URL = "/static/assets/landing/models/ferrari_sf25.min.glb";
const MODEL_URL_FALLBACK = "/static/assets/landing/models/ferrari_sf25.glb";

const TIRE_RUBBER = "#050505";
const TIRE_STRIPE = {
  Soft: "#e10600",
  Medium: "#f5d000",
  Hard: "#f4f4f4",
  Intermediate: "#0b3d91",
  Wet: "#0a5c2e"
};

/** Ключи TIRE_STRIPE — Soft / Medium / Hard. */
export function normalizeTireCompound(raw) {
  const text = String(raw || "Medium").trim();
  if (!text) return "Medium";
  const lower = text.toLowerCase();
  if (lower === "soft" || lower.includes("soft") || text === "Мягкая") return "Soft";
  if (lower === "hard" || lower.includes("hard") || text === "Жёсткая" || text === "Жесткая") return "Hard";
  if (lower === "medium" || lower.includes("medium") || text === "Средняя") return "Medium";
  if (lower === "intermediate" || text === "Промежуточная") return "Intermediate";
  if (lower === "wet" || text === "Дождевые") return "Wet";
  return TIRE_STRIPE[text] ? text : "Medium";
}

/** Ливреи спонсоров: автоэмаль + глянец; carbonMul — затемнение карбоновых панелей. */
const SPONSOR_LIVERY = {
  "SpeedOil": {
    body: "#c8102e", metalness: 0.06, roughness: 0.32, clearcoat: 1, clearcoatRoughness: 0.07,
    envMapIntensity: 1.3, accent: "#6e0818", carbonMul: 0.42
  },
  "MegaTech Engines": {
    body: "#1e56c8", metalness: 0.1, roughness: 0.3, clearcoat: 1, clearcoatRoughness: 0.06,
    envMapIntensity: 1.28, accent: "#0c2258", carbonMul: 0.4
  },
  "SafeDrive": {
    body: "#1f8f4a", metalness: 0.05, roughness: 0.34, clearcoat: 0.98, clearcoatRoughness: 0.08,
    envMapIntensity: 1.2, accent: "#0a3d22", carbonMul: 0.4
  },
  "RainShield Tires": {
    body: "#0f8fa6", metalness: 0.08, roughness: 0.3, clearcoat: 1, clearcoatRoughness: 0.06,
    envMapIntensity: 1.28, accent: "#053844", carbonMul: 0.4
  },
  "FuelMax": {
    body: "#d45f00", metalness: 0.06, roughness: 0.33, clearcoat: 0.98, clearcoatRoughness: 0.07,
    envMapIntensity: 1.25, accent: "#5c2800", carbonMul: 0.42
  },
  "TurboSport": {
    body: "#8e24a8", metalness: 0.1, roughness: 0.3, clearcoat: 1, clearcoatRoughness: 0.06,
    envMapIntensity: 1.28, accent: "#3a0c48", carbonMul: 0.4
  },
  "MediaStar": {
    body: "#5a2fc0", metalness: 0.08, roughness: 0.31, clearcoat: 1, clearcoatRoughness: 0.06,
    envMapIntensity: 1.28, accent: "#24105a", carbonMul: 0.4
  },
  "EcoDrive": {
    body: "#5c9420", metalness: 0.05, roughness: 0.34, clearcoat: 0.95, clearcoatRoughness: 0.09,
    envMapIntensity: 1.18, accent: "#243c0c", carbonMul: 0.4
  },
  "GripMaster Brakes": {
    body: "#2c3038", metalness: 0.58, roughness: 0.36, clearcoat: 0.88, clearcoatRoughness: 0.16,
    envMapIntensity: 1.55, accent: "#101216", carbonMul: 0.72
  },
  "RookieBoost": {
    body: "#168f86", metalness: 0.08, roughness: 0.3, clearcoat: 0.98, clearcoatRoughness: 0.06,
    envMapIntensity: 1.25, accent: "#0a3c3a", carbonMul: 0.4
  }
};

/** Без спонсора: серебристо-белый шоу-кар + тёмный карбон. */
const DEFAULT_LIVERY = {
  body: "#d2d6dc",
  metalness: 0.38,
  roughness: 0.26,
  clearcoat: 1,
  clearcoatRoughness: 0.05,
  envMapIntensity: 1.45,
  accent: "#1a1c20",
  carbonMul: 0.14,
  iridescence: 0.18,
  iridescenceIOR: 1.35
};

const STYLE_UI = [
  { ui: "Агрессивный", data: "Агрессивный" },
  { ui: "Сбалансированный", data: "Сбалансированный" },
  { ui: "Экономичный", data: "Осторожный" }
];

const UPGRADE_MODULES = ["Двигатель", "Управляемость", "Пилот", "Надёжность", "Аэродинамика"];

function clamp(v, min, max) {
  return Math.min(max, Math.max(min, v));
}

function formatBudget(value) {
  return new Intl.NumberFormat("ru-RU").format(Math.round(value));
}

function ensurePaintMaterial(mat) {
  if (mat.userData.__s3dPaintReady) return mat;
  let next = mat;
  if (!mat.isMeshPhysicalMaterial) {
    const phys = new THREE.MeshPhysicalMaterial();
    THREE.MeshStandardMaterial.prototype.copy.call(phys, mat);
    next = phys;
  }
  // Убираем albedo Ferrari — иначе ливрея остаётся «лоскутами»
  next.map = null;
  next.roughnessMap = null;
  next.metalnessMap = null;
  if ("emissiveMap" in next) next.emissiveMap = null;
  // Нормали и AO оставляем — объём кузова выглядит живее
  next.normalMap = mat.normalMap || next.normalMap || null;
  next.aoMap = mat.aoMap || next.aoMap || null;
  next.userData.__s3dPaintReady = true;
  next.userData.__s3dCloned = true;
  return next;
}

/**
 * Автоэмаль: кузов (gloss) или карбон (темнее + чуть матовее).
 * @param {"body"|"carbon"} variant
 */
function applyCarPaint(mat, color, livery, variant = "body") {
  mat = ensurePaintMaterial(mat);
  const paint = color.clone();
  if (variant === "carbon") {
    paint.multiplyScalar(livery.carbonMul ?? 0.4);
  }

  mat.color.copy(paint);
  mat.metalness = variant === "carbon"
    ? Math.min(0.82, (livery.metalness || 0.1) + 0.32)
    : livery.metalness;
  mat.roughness = variant === "carbon"
    ? Math.min(0.58, (livery.roughness || 0.28) + 0.16)
    : livery.roughness;
  mat.clearcoat = variant === "carbon" ? Math.min(0.85, (livery.clearcoat || 1) * 0.75) : livery.clearcoat;
  mat.clearcoatRoughness = variant === "carbon"
    ? Math.min(0.35, (livery.clearcoatRoughness || 0.08) + 0.12)
    : livery.clearcoatRoughness;
  mat.envMapIntensity = (livery.envMapIntensity ?? 1.25) * (variant === "carbon" ? 0.9 : 1);

  if ("specularIntensity" in mat) mat.specularIntensity = variant === "carbon" ? 0.7 : 1;
  if ("ior" in mat) mat.ior = 1.5;

  if ("sheen" in mat) {
    mat.sheen = variant === "body" ? 0.28 : 0.08;
    mat.sheenRoughness = 0.42;
    mat.sheenColor.copy(paint).lerp(new THREE.Color("#ffffff"), 0.25);
  }

  if ("iridescence" in mat) {
    const ir = variant === "body" ? (livery.iridescence || 0) : 0;
    mat.iridescence = ir;
    mat.iridescenceIOR = livery.iridescenceIOR || 1.3;
    mat.iridescenceThicknessRange = [100, 400];
  }

  if ("emissive" in mat) {
    mat.emissive.setHex(0x000000);
    mat.emissiveIntensity = 0;
  }
  if ("transparent" in mat) {
    mat.transparent = false;
    mat.opacity = 1;
    mat.depthWrite = true;
  }
  mat.needsUpdate = true;
  return mat;
}

/** Полная перекраска кузова; шины чёрные + тонкая цветная окантовка. */
export function setModelMaterialColor(root, { tireType, sponsorName, teamColor } = {}) {
  if (!root) return;

  const compound = normalizeTireCompound(tireType);
  const stripeColor = TIRE_STRIPE[compound] || TIRE_STRIPE.Medium;
  let livery;
  if (teamColor) {
    // Цвет команды со stage — сплошная заливка без спонсорской ливреи.
    livery = {
      ...DEFAULT_LIVERY,
      body: teamColor,
      accent: teamColor,
      carbonMul: 0.55,
      metalness: 0.12,
      roughness: 0.34,
      clearcoat: 0.92,
      clearcoatRoughness: 0.1,
      envMapIntensity: 1.2,
      iridescence: 0
    };
  } else {
    livery = (sponsorName && SPONSOR_LIVERY[sponsorName]) || DEFAULT_LIVERY;
  }
  const bodyColor = new THREE.Color(livery.body);
  const accentColor = new THREE.Color(livery.accent || "#111111");

  root.traverse((obj) => {
    if (!obj.isMesh || !obj.material) return;
    const meshName = String(obj.name || "").toLowerCase();
    const materials = Array.isArray(obj.material) ? obj.material : [obj.material];
    const next = materials.map((mat) => {
      if (!mat || !mat.color) return mat;
      if (!mat.userData.__s3dCloned) {
        mat = mat.clone();
        mat.userData.__s3dCloned = true;
      }

      const name = String(mat.name || "").toLowerCase();

      const isTireTread =
        name.includes("tire_tread") ||
        name.includes("tyre_tread") ||
        meshName.includes("tire_tread") ||
        meshName.includes("tyre_tread") ||
        (meshName.includes("tyre") && meshName.includes("tread"));
      const isTireSide =
        name.includes("tire_sides") ||
        name.includes("tire_side") ||
        name.includes("tyre_sides") ||
        name.includes("tyre_side") ||
        meshName.includes("tire_sides") ||
        meshName.includes("tyre_sides") ||
        (meshName.includes("tyre") && (name.includes("side") || meshName.includes("sides")));

      // Не красим стекло/экраны/индикаторы руля (выхлоп, TCAM и стоп — обрабатываем отдельно)
      const isGlassOrLight =
        name.includes("glass") ||
        name.includes("lcd") ||
        name.includes("indicator") ||
        (name.includes("led") && !name.includes("rear_light"));

      const isBrakeLight =
        name.includes("rear_light") ||
        meshName.includes("rear_light") ||
        meshName.includes("rain_light");

      const isDecalOrNumber =
        name.includes("decal") ||
        name.includes("number") ||
        name.includes("ferrari_decal");

      const isPaintShell =
        name.includes("paint") ||
        meshName.includes("paint");

      const isCarbonShell =
        name === "carbon_1" ||
        name === "carbon_2" ||
        name.includes("ferrari_carbon") ||
        name.includes("carbon_halo") ||
        (name.includes("carbon") &&
          !name.includes("caliper") &&
          !name.includes("sw_") &&
          !name.includes("cockpit") &&
          !name.includes("suspension"));

      const isAccent =
        name.includes("ferrari_wheel_hub") ||
        name.includes("carbon_caliper") ||
        meshName.includes("wheel_hub") ||
        meshName.includes("caliper");

      // Белые технические детали → чёрные (в т.ч. выхлоп и TCAM на воздухозаборнике)
      const isExteriorMesh =
        meshName.includes("paint") ||
        meshName.includes("sf-25") ||
        meshName.includes("body") ||
        meshName.includes("chassis");

      const isBlackDetail =
        name.includes("carbon_suspension") ||
        (name.includes("cockpit") && !isExteriorMesh) ||
        (name.includes("generic") && !isExteriorMesh) ||
        name.includes("mirror") ||
        name.includes("exhaust") ||
        name.includes("tcam") ||
        name.startsWith("sw_") ||
        meshName.includes("suspension") ||
        meshName.includes("wishbone") ||
        meshName.includes("mirror") ||
        meshName.includes("cockpit") ||
        meshName.includes("exhaust") ||
        meshName.includes("tcam");

      const DETAIL_BLACK = new THREE.Color("#0a0a0c");
      const BRAKE_RED = new THREE.Color("#ff1a1a");

      if (isTireTread) {
        mat.color.set(TIRE_RUBBER);
        mat.map = null;
        if ("emissiveMap" in mat) mat.emissiveMap = null;
        if ("roughness" in mat) mat.roughness = 0.94;
        if ("metalness" in mat) mat.metalness = 0.02;
        if ("emissive" in mat) {
          mat.emissive.setHex(0x000000);
          mat.emissiveIntensity = 0;
        }
        mat.needsUpdate = true;
      } else if (isTireSide) {
        // Видимая окантовка: базовый цвет шины + яркий emissive по типу резины
        mat.color.set(stripeColor);
        mat.map = null;
        if ("emissiveMap" in mat) mat.emissiveMap = null;
        if ("roughness" in mat) mat.roughness = 0.55;
        if ("metalness" in mat) mat.metalness = 0.08;
        if ("emissive" in mat) {
          mat.emissive.set(stripeColor);
          mat.emissiveIntensity = 0.55;
        }
        if ("toneMapped" in mat) mat.toneMapped = false;
        mat.needsUpdate = true;
      } else if (isBrakeLight) {
        mat.map = null;
        if ("emissiveMap" in mat) mat.emissiveMap = null;
        mat.color.copy(BRAKE_RED);
        if ("metalness" in mat) mat.metalness = 0.05;
        if ("roughness" in mat) mat.roughness = 0.35;
        if ("emissive" in mat) {
          mat.emissive.set("#ff0000");
          mat.emissiveIntensity = 4.5;
        }
        if ("toneMapped" in mat) mat.toneMapped = false;
        if ("transparent" in mat) {
          mat.transparent = false;
          mat.opacity = 1;
        }
        mat.needsUpdate = true;
      } else if (isGlassOrLight) {
        return mat;
      } else if (isDecalOrNumber) {
        mat.transparent = true;
        mat.opacity = 0;
        mat.depthWrite = false;
        mat.color.copy(bodyColor);
        mat.needsUpdate = true;
      } else if (isPaintShell) {
        mat = applyCarPaint(mat, bodyColor, livery, "body");
      } else if (isCarbonShell) {
        mat = applyCarPaint(mat, bodyColor, livery, "carbon");
      } else if (isAccent) {
        mat.map = null;
        if ("roughnessMap" in mat) mat.roughnessMap = null;
        // При teamColor/спонсоре акценты (диски/суппорта) в accent, иначе тёмные
        mat.color.copy((teamColor || sponsorName) ? accentColor : new THREE.Color("#222428"));
        if ("metalness" in mat) mat.metalness = 0.72;
        if ("roughness" in mat) mat.roughness = 0.32;
        if ("emissive" in mat) {
          mat.emissive.setHex(0x000000);
          mat.emissiveIntensity = 0;
        }
        mat.needsUpdate = true;
      } else if (name === "detail" || name === "generics" || name === "generic") {
        // Detail/Generic часто на панелях кузова — в цвет ливреи, не в чёрный.
        mat = applyCarPaint(
          mat,
          bodyColor,
          livery,
          isExteriorMesh || name === "detail" ? "body" : "carbon"
        );
      } else if (isBlackDetail) {
        mat.map = null;
        if ("roughnessMap" in mat) mat.roughnessMap = null;
        if ("metalnessMap" in mat) mat.metalnessMap = null;
        if ("emissiveMap" in mat) mat.emissiveMap = null;
        mat.color.copy(DETAIL_BLACK);
        if ("metalness" in mat) mat.metalness = 0.35;
        if ("roughness" in mat) mat.roughness = 0.52;
        if ("emissive" in mat) {
          mat.emissive.setHex(0x000000);
          mat.emissiveIntensity = 0;
        }
        if ("transparent" in mat && mat.opacity < 1 && !name.includes("glass")) {
          mat.transparent = false;
          mat.opacity = 1;
        }
        mat.needsUpdate = true;
      } else if (teamColor || sponsorName) {
        // Не классифицированные Opaque-части без albedo — в цвет кузова, не дефолт GLB.
        const looksCarbon = name.includes("carbon") && !name.startsWith("sw_");
        mat = applyCarPaint(mat, bodyColor, livery, looksCarbon ? "carbon" : "body");
      }
      return mat;
    });
    obj.material = Array.isArray(obj.material) ? next : next[0];
  });
}

/**
 * Расчёт телеметрии по данным движка (сухая погода как демо-база).
 * Возвращает отображаемые 0–100 для баров + бюджет.
 */
export function computeTelemetry({ tire, style, sponsor, pitStops, upgradesList, activeToken = null }) {
  const dry = 1;
  const wet = 0;

  let speedBonus = style.speed || 0;
  let riskAdjustment = style.failRisk || 0;
  let wearAdjustment = 0;

  speedBonus += (tire.speedDry || 0) * dry + (tire.speedWet || 0) * wet;
  const tyreWearBase = (tire.wearDry || 0) * dry + (tire.wearWet || 0) * wet;
  wearAdjustment += tyreWearBase * 0.1;
  riskAdjustment += (tire.riskDry || 0) * dry + (tire.riskWet || 0) * wet;

  const moduleLevels = {};
  let upgradeCost = 0;
  let pitTimeGain = 0;
  upgradesList.forEach((upgrade) => {
    speedBonus += (upgrade.speedPts || 0) * SPEED_POINTS_FACTOR;
    wearAdjustment += (upgrade.wear || 0) * 0.08;
    const moduleRiskWeight = upgrade.module === "Двигатель" ? 0.3 : 0.5;
    riskAdjustment += (upgrade.risk || 0) * moduleRiskWeight;
    riskAdjustment += (upgrade.error || 0) * 0.5;
    // pit в данных — секунды (отрицательные = быстрее); для шкалы берём выигрыш.
    pitTimeGain += Math.max(0, -(Number(upgrade.pit) || 0));
    pitTimeGain += Math.max(0, -(Number(upgrade.error) || 0)) * 40;
    moduleLevels[upgrade.module] = Math.max(moduleLevels[upgrade.module] || 0, upgrade.level);
    upgradeCost += Number(upgrade.price || 0);
  });

  const engineLevel = moduleLevels["Двигатель"] || 0;
  if (engineLevel > 0) {
    speedBonus += engineLevel * 0.006;
    wearAdjustment += engineLevel * 0.002;
    riskAdjustment += Math.max(0, engineLevel - 1) * 0.002;
  }
  const reliabilityLevel = moduleLevels["Надёжность"] || 0;
  if (reliabilityLevel > 0) {
    riskAdjustment -= reliabilityLevel * 0.008;
    wearAdjustment -= reliabilityLevel * 0.004;
  }
  const pilotLevel = moduleLevels["Пилот"] || 0;
  if (pilotLevel > 0) {
    riskAdjustment -= pilotLevel * 0.014;
    speedBonus += pilotLevel * 0.002;
  }
  const handlingLevel = moduleLevels["Управляемость"] || 0;
  if (handlingLevel > 0) {
    wearAdjustment -= handlingLevel * 0.004;
    riskAdjustment -= handlingLevel * 0.003;
  }
  const aeroLevel = moduleLevels["Аэродинамика"] || 0;
  if (aeroLevel > 0) {
    speedBonus += aeroLevel * 0.0025;
  }

  if (sponsor) {
    speedBonus += (sponsor.post_speedPts || 0) * SPEED_POINTS_FACTOR;
    wearAdjustment += (sponsor.post_wearAdd || 0) * 0.1;
    riskAdjustment += sponsor.post_riskAdd || 0;
  }

  // Как в raceEngine: активный токен команды влияет на темп / износ / риск / пит.
  const tokenEffect = activeToken?.effect && typeof activeToken.effect === "object"
    ? activeToken.effect
    : null;
  if (tokenEffect) {
    speedBonus += Number(tokenEffect.speedBonus || 0) || 0;
    wearAdjustment += Number(tokenEffect.wearAdjustment || 0) || 0;
    riskAdjustment += Number(tokenEffect.riskAdjustment || 0) || 0;
    // pitTimeModifier в секундах (отрицательные = быстрее) → вклад в шкалу «Пит».
    pitTimeGain += Math.max(0, -(Number(tokenEffect.pitTimeModifier || 0) || 0));
  }

  const trackWearBase = 0.03 * (style.degMult ?? 1);
  let wearRate = trackWearBase + wearAdjustment;
  const pits = Number(pitStops) || 0;
  if (pits <= 0) {
    wearRate *= 1.1;
  } else {
    wearRate *= 1 - Math.min(0.12, Math.max(0, pits - 1) * 0.04);
  }
  riskAdjustment += Math.max(0, pits - 2) * 0.008;

  const speed = clamp(58 + speedBonus * 900, 5, 100);
  const wear = clamp(28 + wearRate * 650, 5, 100);
  const risk = clamp(32 + riskAdjustment * 320, 5, 100);
  // Деньги спонсора в игре приходят только после гонки, поэтому в бюджет до старта не входят.
  const budget = BASE_BUDGET - upgradeCost;

  const pitBoxLevel = moduleLevels["Пит-бокс"] || 0;
  let pitScore = 36 + pitTimeGain * 5.5 + pitBoxLevel * 12;
  if (pits >= 3) pitScore += 8 + pitBoxLevel * 2;
  else if (pits === 2) pitScore += 5;
  else if (pits === 1) pitScore += 2;
  else pitScore -= 10;
  if (tire?.type === "Soft") pitScore += 4;
  const pit = clamp(pitScore, 5, 100);

  return {
    speed: Math.round(speed),
    wear: Math.round(wear),
    risk: Math.round(risk),
    pit: Math.round(pit),
    budget,
    raw: { speedBonus, wearRate, riskAdjustment, pitTimeGain }
  };
}

async function loadEngineData() {
  const [tires, styles, sponsors, upgrades] = await Promise.all([
    fetch("/data/tires.json").then((r) => r.json()),
    fetch("/data/styles.json").then((r) => r.json()),
    fetch("/data/sponsors.json").then((r) => r.json()),
    fetch("/data/upgrades.json").then((r) => r.json())
  ]);
  return {
    tires: tires.filter((t) => ["Soft", "Medium", "Hard"].includes(t.type)),
    styles,
    sponsors,
    upgrades
  };
}

function buildUI(root, data) {
  const tireOpts = data.tires.map((t) =>
    `<button type="button" class="s3d-chip" data-tire="${t.type}"><span>${t.type}</span></button>`
  ).join("");

  const styleOpts = STYLE_UI.map((s) =>
    `<button type="button" class="s3d-chip" data-style="${s.data}"><span>${s.ui}</span></button>`
  ).join("");

  const pitOpts = [1, 2, 3].map((n) =>
    `<button type="button" class="s3d-chip s3d-chip--num" data-pits="${n}"><span>${n}</span></button>`
  ).join("");

  const sponsorItems = [`<li role="option" class="s3d-select-option is-active" data-value="" aria-selected="true">Без спонсора</li>`].concat(
    data.sponsors.map((s) =>
      `<li role="option" class="s3d-select-option" data-value="${s.name}" aria-selected="false">${s.name}</li>`
    )
  ).join("");

  const upgradeRows = UPGRADE_MODULES.map((mod) => {
    const levels = [0, 1, 2, 3].map((lvl) =>
      `<button type="button" class="s3d-chip s3d-chip--sm" data-upgrade="${mod}" data-level="${lvl}"><span>${lvl === 0 ? "—" : "L" + lvl}</span></button>`
    ).join("");
    return `<div class="s3d-upgrade-row"><span class="s3d-upgrade-name">${mod}</span><div class="s3d-chips">${levels}</div></div>`;
  }).join("");

  root.innerHTML = `
    <div class="s3d-head">
      <div class="s3d-kicker"><i class="s3d-pulse" aria-hidden="true"></i> SYS // STRATEGY.CONSOLE</div>
      <div class="s3d-head-row">
        <h3 class="s3d-title">Стратегия на заезд</h3>
      </div>
    </div>
    <div class="s3d-grid">
      <aside class="s3d-controls">
        <div class="s3d-panel-tag">01 // CONFIG</div>
        <div class="s3d-field">
          <label>Тип шин</label>
          <div class="s3d-chips" data-group="tire">${tireOpts}</div>
        </div>
        <div class="s3d-field">
          <label>Стиль вождения</label>
          <div class="s3d-chips" data-group="style">${styleOpts}</div>
        </div>
        <div class="s3d-field s3d-field--inline">
          <div class="s3d-inline-col">
            <label>Пит-стопы</label>
            <div class="s3d-chips" data-group="pits">${pitOpts}</div>
          </div>
          <div class="s3d-inline-col s3d-field-grow">
            <label id="s3dSponsorLabel">Спонсор</label>
            <div class="s3d-select-wrap" data-sponsor-wrap>
              <button
                type="button"
                class="s3d-select"
                data-sponsor-trigger
                aria-haspopup="listbox"
                aria-expanded="false"
                aria-labelledby="s3dSponsorLabel"
              >
                <span data-sponsor-label>Без спонсора</span>
              </button>
              <ul class="s3d-select-menu" data-sponsor-menu role="listbox" aria-labelledby="s3dSponsorLabel" hidden>
                ${sponsorItems}
              </ul>
            </div>
          </div>
        </div>
        <div class="s3d-field">
          <label>Апгрейды</label>
          <div class="s3d-upgrades">${upgradeRows}</div>
        </div>
      </aside>
      <div class="s3d-stage">
        <div class="s3d-canvas-wrap">
          <div class="s3d-hud-corners" aria-hidden="true">
            <i class="tl"></i><i class="tr"></i><i class="bl"></i><i class="br"></i>
          </div>
          <div class="s3d-hud-top">
            <span>VIEW // 3D.MODEL</span>
            <span class="s3d-live"><i></i> LIVE</span>
          </div>
          <div class="s3d-grid-bg" aria-hidden="true"></div>
          <div class="s3d-scan" aria-hidden="true"></div>
          <canvas class="s3d-canvas" aria-label="3D модель болида"></canvas>
          <div class="s3d-loading">SYNC // MODEL…</div>
          <div class="s3d-car-meta">
            <span data-meta-tire>Medium</span>
            <span data-meta-sponsor>—</span>
          </div>
        </div>
      </div>
      <div class="s3d-telemetry">
        <div class="s3d-meter" data-meter="speed">
          <div class="s3d-meter-code">SPD</div>
          <div class="s3d-meter-body">
            <div class="s3d-meter-top"><span>Темп на круге</span><strong>0</strong></div>
            <div class="s3d-meter-bar"><i></i></div>
          </div>
        </div>
        <div class="s3d-meter" data-meter="wear">
          <div class="s3d-meter-code">WR</div>
          <div class="s3d-meter-body">
            <div class="s3d-meter-top"><span>Износ</span><strong>0</strong></div>
            <div class="s3d-meter-bar"><i></i></div>
          </div>
        </div>
        <div class="s3d-meter" data-meter="risk">
          <div class="s3d-meter-code">RSK</div>
          <div class="s3d-meter-body">
            <div class="s3d-meter-top"><span>Риск</span><strong>0</strong></div>
            <div class="s3d-meter-bar"><i></i></div>
          </div>
        </div>
        <div class="s3d-meter" data-meter="budget">
          <div class="s3d-meter-code">BUD</div>
          <div class="s3d-meter-body">
            <div class="s3d-meter-top"><span>Бюджет</span><strong>0 ₽</strong></div>
            <div class="s3d-meter-bar s3d-meter-bar--budget"><i></i></div>
          </div>
        </div>
      </div>
    </div>
  `;
}

export function createViewer(canvas, onReady) {
  const wrap = canvas.parentElement;
  const mobileStart = window.matchMedia("(max-width: 1024px)").matches;
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: true,
    powerPreference: "default"
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.shadowMap.enabled = !mobileStart;
  if (!mobileStart) renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.18;

  const scene = new THREE.Scene();
  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.02).texture;
  if ("environmentIntensity" in scene) scene.environmentIntensity = 1.15;
  scene.fog = new THREE.Fog(0x07080c, 9, 24);

  const camera = new THREE.PerspectiveCamera(35, 1, 0.1, 100);
  const controls = new OrbitControls(camera, canvas);
  controls.enableDamping = !mobileStart;
  controls.autoRotate = true;
  controls.autoRotateSpeed = mobileStart ? 0.45 : 0.7;
  controls.rotateSpeed = 0.85;
  controls.zoomSpeed = 0.9;
  controls.maxPolarAngle = Math.PI * 0.49;

  // Desktop: чуть дальше текущего крупного плана. Mobile: сильный zoom-out.
  const CAM_DESKTOP = { pos: [1.72, 0.95, 2.2], min: 1.2, max: 5.5, fov: 35, targetY: 0.36 };
  const CAM_MOBILE = { pos: [2.53, 1.32, 3.25], min: 2.25, max: 6.5, fov: 37, targetY: 0.45 };
  let framingMode = null;
  let autoRotateResumeTimer = 0;

  function isMobileViewport() {
    // Важно: смотрим на окно, НЕ на ширину canvas-колонки (на ПК она часто < 1025)
    return window.matchMedia("(max-width: 1024px)").matches;
  }

  function applyCameraFraming(force = false) {
    const mobile = isMobileViewport();
    const mode = mobile ? "mobile" : "desktop";
    if (!force && mode === framingMode) return;
    framingMode = mode;
    const cfg = mobile ? CAM_MOBILE : CAM_DESKTOP;
    camera.fov = cfg.fov;
    camera.position.set(cfg.pos[0], cfg.pos[1], cfg.pos[2]);
    controls.minDistance = cfg.min;
    controls.maxDistance = cfg.max;
    controls.target.set(0, cfg.targetY, 0);
    // ПК: крутить и зумить. Мобилка: скролл страницы, только автоповорот.
    controls.enableRotate = !mobile;
    controls.enableZoom = !mobile;
    controls.enablePan = false;
    controls.enableDamping = !mobile;
    controls.autoRotate = true;
    canvas.style.touchAction = mobile ? "pan-y" : "none";
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    camera.updateProjectionMatrix();
    controls.update();
  }

  function pauseAutoRotateForInteraction() {
    if (framingMode !== "desktop") return;
    controls.autoRotate = false;
    window.clearTimeout(autoRotateResumeTimer);
    autoRotateResumeTimer = window.setTimeout(() => {
      if (framingMode === "desktop") controls.autoRotate = true;
    }, 3500);
  }

  controls.addEventListener("start", pauseAutoRotateForInteraction);
  canvas.addEventListener("wheel", pauseAutoRotateForInteraction, { passive: true });
  window.addEventListener("resize", () => applyCameraFraming(false));

  applyCameraFraming(true);

  const hemi = new THREE.HemisphereLight(0xf2f4f8, 0x1a1c22, 0.95);
  scene.add(hemi);
  const key = new THREE.DirectionalLight(0xfff5ea, mobileStart ? 2.0 : 2.35);
  key.position.set(3.8, 7.2, 2.8);
  key.castShadow = !mobileStart;
  if (!mobileStart) key.shadow.mapSize.set(1024, 1024);
  scene.add(key);
  const fill = new THREE.DirectionalLight(0xdce6ff, 0.75);
  fill.position.set(-3.2, 2.8, 3.5);
  scene.add(fill);
  if (!mobileStart) {
    const rim = new THREE.DirectionalLight(0xff6a5c, 0.28);
    rim.position.set(-4, 2.2, -3.2);
    scene.add(rim);
    const bounce = new THREE.DirectionalLight(0xffffff, 0.35);
    bounce.position.set(0.5, 0.2, -2.5);
    scene.add(bounce);
  }

  const ground = new THREE.Mesh(
    new THREE.CircleGeometry(5, mobileStart ? 48 : 64),
    new THREE.MeshStandardMaterial({
      color: 0x12141a,
      metalness: 0.55,
      roughness: 0.42,
      envMapIntensity: 0.8
    })
  );
  ground.rotation.x = -Math.PI / 2;
  ground.receiveShadow = !mobileStart;
  scene.add(ground);

  let carRoot = null;
  const loader = new GLTFLoader();
  if (MeshoptDecoder) {
    loader.setMeshoptDecoder(MeshoptDecoder);
  }

  function mountCar(gltf) {
    carRoot = gltf.scene;
    carRoot.traverse((obj) => {
      if (obj.isMesh) {
        obj.castShadow = !mobileStart;
        obj.receiveShadow = !mobileStart;
      }
    });
    const box = new THREE.Box3().setFromObject(carRoot);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const scale = 3.05 / Math.max(size.x, size.y, size.z);
    carRoot.scale.setScalar(scale);
    carRoot.position.sub(center.multiplyScalar(scale));
    carRoot.position.y = 0.02;
    scene.add(carRoot);
    onReady(carRoot);
  }

  function failModel(err) {
    console.error("GLB load failed", err);
    const loading = wrap.querySelector(".s3d-loading");
    if (loading) loading.textContent = "Не удалось загрузить модель";
  }

  loader.load(
    MODEL_URL,
    mountCar,
    undefined,
    (err) => {
      console.warn("min GLB failed, trying original", err);
      loader.load(MODEL_URL_FALLBACK, mountCar, undefined, failModel);
    }
  );

  function resize() {
    const w = wrap.clientWidth || 640;
    const h = wrap.clientHeight || 360;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    applyCameraFraming(false);
    camera.updateProjectionMatrix();
  }

  resize();
  const ro = new ResizeObserver(resize);
  ro.observe(wrap);

  let visible = true;
  let raf = 0;
  const tick = () => {
    raf = 0;
    if (!visible) return;
    controls.update();
    renderer.render(scene, camera);
    raf = requestAnimationFrame(tick);
  };

  const visIo = new IntersectionObserver(
    (entries) => {
      const next = entries.some((e) => e.isIntersecting);
      visible = next;
      if (visible && !raf) raf = requestAnimationFrame(tick);
    },
    { root: null, rootMargin: "80px", threshold: 0.01 }
  );
  visIo.observe(wrap);
  raf = requestAnimationFrame(tick);

  return {
    getRoot: () => carRoot,
    dispose() {
      cancelAnimationFrame(raf);
      raf = 0;
      visible = false;
      window.clearTimeout(autoRotateResumeTimer);
      visIo.disconnect();
      ro.disconnect();
      controls.dispose();
      renderer.dispose();
    }
  };
}

function markActive(group, attr, value) {
  group.querySelectorAll(`[${attr}]`).forEach((btn) => {
    btn.classList.toggle("is-active", btn.getAttribute(attr) === String(value));
  });
}

export async function initLandingStrategy3D(rootEl) {
  if (!rootEl) return;
  const data = await loadEngineData();
  buildUI(rootEl, data);

  const state = {
    tire: "Medium",
    style: "Сбалансированный",
    pitStops: 2,
    sponsor: "",
    upgrades: Object.fromEntries(UPGRADE_MODULES.map((m) => [m, 0]))
  };

  const canvas = rootEl.querySelector(".s3d-canvas");
  const loading = rootEl.querySelector(".s3d-loading");
  let carRoot = null;
  await loadThreeDependencies();

  const viewer = createViewer(canvas, (root) => {
    carRoot = root;
    loading?.remove();
    applyAll();
  });

  function resolveUpgradeList() {
    const list = [];
    UPGRADE_MODULES.forEach((mod) => {
      const level = state.upgrades[mod] || 0;
      if (!level) return;
      const row = data.upgrades.find((u) => u.module === mod && u.level === level);
      if (row) list.push(row);
    });
    return list;
  }

  function applyAll() {
    const tire = data.tires.find((t) => t.type === state.tire) || data.tires[1];
    const style = data.styles.find((s) => s.name === state.style) || data.styles[1];
    const sponsor = state.sponsor
      ? (data.sponsors.find((s) => s.name === state.sponsor) || null)
      : null;
    const upgradesList = resolveUpgradeList();
    const telemetry = computeTelemetry({
      tire,
      style,
      sponsor,
      pitStops: state.pitStops,
      upgradesList
    });

    // sync UI active states
    markActive(rootEl.querySelector('[data-group="tire"]'), "data-tire", state.tire);
    markActive(rootEl.querySelector('[data-group="style"]'), "data-style", state.style);
    markActive(rootEl.querySelector('[data-group="pits"]'), "data-pits", state.pitStops);
    UPGRADE_MODULES.forEach((mod) => {
      rootEl.querySelectorAll(`[data-upgrade="${mod}"]`).forEach((btn) => {
        btn.classList.toggle("is-active", Number(btn.dataset.level) === state.upgrades[mod]);
      });
    });
    const sponsorLabel = rootEl.querySelector("[data-sponsor-label]");
    const sponsorOptions = rootEl.querySelectorAll("[data-sponsor-menu] [data-value]");
    if (sponsorLabel) sponsorLabel.textContent = state.sponsor || "Без спонсора";
    sponsorOptions.forEach((opt) => {
      const active = (opt.getAttribute("data-value") || "") === (state.sponsor || "");
      opt.classList.toggle("is-active", active);
      opt.setAttribute("aria-selected", active ? "true" : "false");
    });

    const metaTire = rootEl.querySelector("[data-meta-tire]");
    const metaSponsor = rootEl.querySelector("[data-meta-sponsor]");
    if (metaTire) metaTire.textContent = state.tire;
    if (metaSponsor) metaSponsor.textContent = state.sponsor || "Без спонсора";

    // meters
    ["speed", "wear", "risk"].forEach((key) => {
      const meter = rootEl.querySelector(`[data-meter="${key}"]`);
      if (!meter) return;
      const val = telemetry[key];
      meter.querySelector("strong").textContent = String(val);
      meter.querySelector("i").style.width = `${val}%`;
    });
    const budgetMeter = rootEl.querySelector('[data-meter="budget"]');
    if (budgetMeter) {
      budgetMeter.querySelector("strong").textContent = formatBudget(telemetry.budget);
      const budgetPct = clamp((telemetry.budget / BASE_BUDGET) * 100, 8, 100);
      budgetMeter.querySelector("i").style.width = `${budgetPct}%`;
    }

    if (carRoot) {
      setModelMaterialColor(carRoot, { tireType: state.tire, sponsorName: state.sponsor });
    }
  }

  rootEl.querySelector('[data-group="tire"]').addEventListener("click", (e) => {
    const btn = e.target.closest("[data-tire]");
    if (!btn) return;
    state.tire = btn.dataset.tire;
    applyAll();
  });
  rootEl.querySelector('[data-group="style"]').addEventListener("click", (e) => {
    const btn = e.target.closest("[data-style]");
    if (!btn) return;
    state.style = btn.dataset.style;
    applyAll();
  });
  rootEl.querySelector('[data-group="pits"]').addEventListener("click", (e) => {
    const btn = e.target.closest("[data-pits]");
    if (!btn) return;
    state.pitStops = Number(btn.dataset.pits);
    applyAll();
  });
  const sponsorWrap = rootEl.querySelector("[data-sponsor-wrap]");
  const sponsorTrigger = rootEl.querySelector("[data-sponsor-trigger]");
  const sponsorMenu = rootEl.querySelector("[data-sponsor-menu]");

  function setSponsorMenuOpen(open) {
    if (!sponsorWrap || !sponsorTrigger || !sponsorMenu) return;
    sponsorWrap.classList.toggle("is-open", open);
    sponsorTrigger.setAttribute("aria-expanded", open ? "true" : "false");
    if (open) sponsorMenu.removeAttribute("hidden");
    else sponsorMenu.setAttribute("hidden", "");
  }

  sponsorTrigger?.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSponsorMenuOpen(!sponsorWrap.classList.contains("is-open"));
  });

  sponsorMenu?.addEventListener("click", (e) => {
    const opt = e.target.closest("[data-value]");
    if (!opt) return;
    e.preventDefault();
    e.stopPropagation();
    state.sponsor = opt.getAttribute("data-value") || "";
    setSponsorMenuOpen(false);
    applyAll();
  });

  document.addEventListener("click", (e) => {
    if (!sponsorWrap || !sponsorWrap.classList.contains("is-open")) return;
    if (sponsorWrap.contains(e.target)) return;
    setSponsorMenuOpen(false);
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") setSponsorMenuOpen(false);
  });

  rootEl.querySelector(".s3d-upgrades").addEventListener("click", (e) => {
    const btn = e.target.closest("[data-upgrade]");
    if (!btn) return;
    state.upgrades[btn.dataset.upgrade] = Number(btn.dataset.level);
    applyAll();
  });

  // initial UI sync before model arrives
  applyAll();

  return () => viewer.dispose();
}

// auto-boot
const mount = document.getElementById("landingStrategy3d");
if (mount) {
  let started = false;
  const boot = () => {
    if (started) return;
    started = true;
    initLandingStrategy3D(mount).catch((err) => {
      console.error(err);
      mount.innerHTML = `<p class="s3d-error">Не удалось инициализировать 3D-блок стратегии.</p>`;
    });
  };

  if ("IntersectionObserver" in window) {
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      boot();
    }, { rootMargin: "800px 0px" });
    observer.observe(mount);
  } else {
    boot();
  }
}
